import { useCallback, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';

import {
  fetchGameweekMatches,
  type GameweekMatchSummary
} from '@api-service/live-matches';
import { fetchSnapshot } from '@lib/live/match/snapshot';
import { fillSquadFromRosters } from '@lib/live/harness/fill';
import { toRoster, type Roster } from '@lib/live/harness/roster';
import {
  validateHarnessSquad,
  type HarnessSquad
} from '@lib/live/harness/squad';
import type { FplPosition } from '@lib/live/team/types';
import type { MatchSnapshot } from '@lib/live/match/types';

/**
 * How often an attached fixture's snapshot is re-read while the panel is open.
 *
 * The readout exists so an operator can confirm a simulation is running and
 * that the app is looking at the right one, which means the clock has to move.
 * Ten seconds is frequent enough to see that without adding meaningful load to
 * an endpoint the pitch is already polling on its own schedule.
 */
const SNAPSHOT_POLL_MS = 10_000;

export type AttachedFixture = {
  matchId: string;
  status: MatchSnapshot['status'];
  provenance: string | null;
  /** Engine-side minute, absent before kickoff and on a finalised match. */
  minute: number | null;
  homeTeam: string;
  awayTeam: string;
  score: string;
};

const toAttached = (snapshot: MatchSnapshot): AttachedFixture => {
  // Only a live or finalised snapshot carries a clock; a pre-match one has
  // nothing to report yet.
  const clock = 'clock' in snapshot ? snapshot.clock : undefined;
  return {
    matchId: snapshot.matchId,
    status: snapshot.status,
    provenance: snapshot.provenance ?? null,
    minute: clock?.displayMinute ?? clock?.minute ?? null,
    homeTeam: snapshot.homeTeam.name,
    awayTeam: snapshot.awayTeam.name,
    score: `${snapshot.homeTeam.score}-${snapshot.awayTeam.score}`
  };
};

export type ShortfallByPosition = Record<FplPosition, number>;

export type HarnessBuilder = {
  gameweek: number;
  setGameweek: (gameweek: number) => void;
  fixtures: GameweekMatchSummary[];
  isLoadingFixtures: boolean;
  fixturesError: string | null;
  selectedMatchIds: string[];
  toggleMatch: (matchId: string) => void;
  rosters: Roster[];
  isLoadingRosters: boolean;
  attached: AttachedFixture[];
  draft: HarnessSquad | null;
  setDraft: (squad: HarnessSquad | null) => void;
  fill: () => void;
  shortfall: ShortfallByPosition | null;
  problems: string[];
};

/**
 * Everything the panel needs to assemble a squad: the gameweek's fixtures, the
 * rosters of whichever are selected, a draft squad and why it is not yet
 * usable.
 *
 * Rosters come from the match snapshots, which is the only place a fixture's
 * team sheet exists on this side. That is also what keeps the built squad
 * honest: every selectable player is one the engine is publishing a channel
 * for, with the ids the pitch joins on.
 */
export const useHarnessBuilder = (
  accessToken: string,
  defaultGameweek: number
): HarnessBuilder => {
  const [gameweek, setGameweek] = useState(defaultGameweek);
  const [selectedMatchIds, setSelectedMatchIds] = useState<string[]>([]);
  const [draft, setDraft] = useState<HarnessSquad | null>(null);
  const [shortfall, setShortfall] = useState<ShortfallByPosition | null>(null);

  const fixturesQuery = useQuery({
    queryKey: ['harness-fixtures', gameweek],
    queryFn: async () => fetchGameweekMatches(gameweek, accessToken),
    enabled: Boolean(accessToken) && gameweek > 0,
    retry: false
  });

  const snapshotQueries = useQueries({
    queries: selectedMatchIds.map((matchId) => ({
      queryKey: ['harness-snapshot', matchId],
      queryFn: async () => fetchSnapshot(matchId, accessToken),
      enabled: Boolean(accessToken),
      refetchInterval: SNAPSHOT_POLL_MS,
      retry: false
    }))
  });

  // Derived on every render rather than memoised. `useQueries` returns a fresh
  // array each time, so any dependency list over it would either recompute
  // anyway or need a hand-built key that silently goes stale. At two fixtures
  // and seventy players this costs nothing worth defending against.
  const snapshots = snapshotQueries
    .map((query) => query.data)
    .filter((snapshot): snapshot is MatchSnapshot => Boolean(snapshot));

  const rosters = snapshots.map(toRoster);
  const attached = snapshots.map(toAttached);

  const toggleMatch = useCallback((matchId: string) => {
    setSelectedMatchIds((previous) =>
      previous.includes(matchId)
        ? previous.filter((one) => one !== matchId)
        : [...previous, matchId]
    );
    // The draft was built against a different set of fixtures, so its picks
    // may carry match ids that are no longer attached. Clearing is honest;
    // silently keeping it would leave picks pointing at nothing.
    setDraft(null);
    setShortfall(null);
  }, []);

  /**
   * Changing gameweek drops everything chosen under the previous one.
   *
   * The selections are fixture ids, and they survive a gameweek change unless
   * cleared: their snapshots keep polling and a fill would then build a squad
   * labelled with the new gameweek out of the old one's fixtures. Nothing
   * about that is visible on screen, which is what makes it worth clearing
   * rather than warning about.
   */
  const selectGameweek = useCallback((next: number) => {
    setGameweek(next);
    setSelectedMatchIds([]);
    setDraft(null);
    setShortfall(null);
  }, []);

  const fill = useCallback(() => {
    const result = fillSquadFromRosters(rosters, gameweek);
    if ('squad' in result) {
      setDraft(result.squad);
      setShortfall(null);
      return;
    }
    setDraft(null);
    setShortfall(result.shortfall);
  }, [rosters, gameweek]);

  const problems = useMemo(
    () => (draft === null ? [] : validateHarnessSquad(draft)),
    [draft]
  );

  return {
    gameweek,
    setGameweek: selectGameweek,
    fixtures: fixturesQuery.data ?? [],
    isLoadingFixtures: fixturesQuery.isInitialLoading,
    fixturesError:
      fixturesQuery.error instanceof Error ? fixturesQuery.error.message : null,
    selectedMatchIds,
    toggleMatch,
    rosters,
    isLoadingRosters: snapshotQueries.some((query) => query.isInitialLoading),
    attached,
    draft,
    setDraft,
    fill,
    shortfall,
    problems
  };
};
